import { DotFilledIcon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Heading, Text } from '@radix-ui/themes';
import { DiamondCanvas } from 'classes/diamond-canvas';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HPSummarySectionCard } from 'components/sections/hitter-library/profile/summary/sections/section-card';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { HIT_OUTCOME_PRIORITY, OutcomeCode } from 'lib_ts/enums/hitters.enums';
import { MAX_HIT_DISTANCE_FT } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISHHitDetails } from 'lib_ts/interfaces/session-hits/i-hit-details';
import { isNil, orderBy } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';

const FIELD_SVG_URL = '/img/spray-field-vector.svg';
const REDRAW_DEQUEUE_MS = 100;
const SHOW_CANVAS_FIELD = false;

interface IHitSprayLegendProps {
  outcomes: OutcomeCode[];
}

const HitSprayLegend = (props: IHitSprayLegendProps) => {
  const { outcomes } = props;

  return (
    <Flex
      direction="column"
      gap={RADIX.FLEX.GAP.XS}
      position="absolute"
      bottom="0"
      mb="7"
    >
      {outcomes.map((outcome) => (
        <Flex
          gap={RADIX.FLEX.GAP.XS}
          key={`legend-item-${outcome}`}
          className="cursor-pointer"
          align="center"
        >
          <DotFilledIcon
            color={HitHelper.getOutcomeColor(outcome)}
            height="20"
            width="20"
          />
          <Box display={{ initial: 'none', lg: 'block' }}>
            <Text color={RADIX.COLOR.SECONDARY}>
              {HitHelper.getOutcomeLabel(outcome)}
            </Text>
          </Box>
          <Box display={{ initial: 'block', lg: 'none' }}>
            <Text color={RADIX.COLOR.SECONDARY}>{outcome}</Text>
          </Box>
        </Flex>
      ))}
    </Flex>
  );
};

export const HPSummaryHitSpray = () => {
  const currSummaryHits = useHitterProfileStore((s) => s.currSummaryHits);
  const hitsFilter = useHitterProfileStore((s) => s.hitsFilter);
  const loading = useHitterProfileStore((s) => s.loading);

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const fieldCanvas = useRef<HTMLCanvasElement>(null);
  const hitCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();

  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(
    () =>
      DiamondCanvas.makeWidget({
        isDark: isDarkMode,
        ftToPx: 1,
        minXFt: -288,
        maxXFt: 288,
        minYFt: -24,
        maxYFt: 480,
        widthScale: 1,
      }),
    [isDarkMode]
  );

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [fieldCanvas.current, hitCanvas.current].filter(
        (c) => c
      ) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawField
  useEffect(() => {
    if (!SHOW_CANVAS_FIELD) {
      return;
    }

    try {
      const canvas = fieldCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      canvasDef.drawField(ctx);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [canvasDef]);

  const data = useMemo(() => {
    // remove hits we've deemed invalid or the user has excluded from averages
    const output = currSummaryHits
      .filter((m) => !m.hit?.invalid)
      .filter(
        // handling for legacy data that hasn't been marked invalid but has extreme distance
        (m) => !m.hit?.distanceFT || m.hit?.distanceFT <= MAX_HIT_DISTANCE_FT
      )
      .filter((m) => !m.avg_excluded)
      .map((m) => m.hit)
      .filter(
        (h) => h && DiamondCanvas.canDrawHit(h)
      ) as Partial<ISHHitDetails>[];

    return output;
  }, [currSummaryHits, hitsFilter.period]);

  // drawHit
  useEffect(() => {
    try {
      const canvas = hitCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      data.forEach((hit) => {
        canvasDef.drawHit(ctx, hit, {
          skipArc: true,
          border: true,
          size: 5,
        });
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [canvasDef, data]);

  const uniqueOutcomes = useMemo(() => {
    const uniqueOutcomes = ArrayHelper.unique(
      data.map((m) => m._mx_outcome || HitHelper.getOutcomeLegacy(m))
    ).filter((m) => !isNil(m));

    const sorted = orderBy(uniqueOutcomes, (v) =>
      HIT_OUTCOME_PRIORITY.indexOf(v)
    );

    return sorted;
  }, [data]);

  return (
    <ErrorBoundary identifier="HitterProfileSummaryHitSpray">
      <HPSummarySectionCard loading={loading}>
        <Flex justify="between" align="center">
          <Heading size="3">Hit Spray Chart</Heading>
          <Badge>{data.length} Total Hits</Badge>
        </Flex>
        <Text color={RADIX.COLOR.NEUTRAL}>Detailed hit location chart</Text>

        <Flex
          maxHeight={{ lg: '400px', initial: '350px' }}
          height="100%"
          align="center"
          justify="center"
        >
          <div
            className="DiamondWidget"
            style={{
              aspectRatio: canvasDef.CONFIG.canvas.aspectRatio,
              position: 'relative',
              width: 'auto',
              height: '100%',
              marginBottom: '32px',
            }}
          >
            {/* for log rocket recordings to show the results */}
            {imgSrc && <img src={imgSrc} />}
            <canvas
              ref={mergeCanvas}
              width={canvasDef.CONFIG.canvas.widthPx}
              height={canvasDef.CONFIG.canvas.heightPx}
            />
            {SHOW_CANVAS_FIELD && (
              <canvas
                ref={fieldCanvas}
                width={canvasDef.CONFIG.canvas.widthPx}
                height={canvasDef.CONFIG.canvas.heightPx}
              />
            )}
            <canvas
              ref={hitCanvas}
              width={canvasDef.CONFIG.canvas.widthPx}
              height={canvasDef.CONFIG.canvas.heightPx}
            />
            <img
              src={FIELD_SVG_URL}
              style={{
                position: 'absolute',
                display: 'block',
                width: '85%',
                left: '50%',
                top: '5%',
                transform: 'translateX(-50%)',
                zIndex: -1,
              }}
            />
          </div>
        </Flex>
        <HitSprayLegend outcomes={uniqueOutcomes} />
      </HPSummarySectionCard>
    </ErrorBoundary>
  );
};
